import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton, 
  Tooltip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import CommentIcon from '@mui/icons-material/Comment';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CollectionsIcon from '@mui/icons-material/Collections';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { supabase } from '../supabaseClient';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.03)' 
      : 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.04)',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const QRCodeDialog = ({ open, onClose, shareUrl }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  useEffect(() => {
    if (open && shareUrl) {
      // Generate QR code using a free API
      const encodedUrl = encodeURIComponent(shareUrl);
      setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedUrl}`);
    }
  }, [open, shareUrl]);
  
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>QR Kod</DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', p: 2 }}>
          {qrCodeUrl ? (
            <>
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                style={{ width: 200, height: 200 }} 
              />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Bu QR kodu taratarak paylaşıma erişebilirsiniz
              </Typography>
            </>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
        <Button 
          variant="contained" 
          onClick={() => {
            // Download QR code
            const link = document.createElement('a');
            link.href = qrCodeUrl;
            link.download = 'sanalyapi-qrcode.png';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          disabled={!qrCodeUrl}
        >
          İndir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SharedLinks = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sharedLinks, setSharedLinks] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedShareUrl, setSelectedShareUrl] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedShare, setSelectedShare] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signup', {
          state: {
            returnUrl: '/shared-links',
            message: 'Paylaşımlarınızı görüntülemek için lütfen giriş yapın'
          }
        });
        return;
      }
      
      fetchSharedLinks(session.user.id);
    };
    
    checkAuth();
  }, [navigate]);

  const fetchSharedLinks = async (userId) => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('shared_links')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setSharedLinks(data || []);
    } catch (err) {
      console.error('Error fetching shared links:', err);
      setError('Paylaşım bağlantıları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, share) => {
    setAnchorEl(event.currentTarget);
    setSelectedShare(share);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      setSuccess('Bağlantı panoya kopyalandı!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    });
  };

  const handleShowQrCode = (shareUrl) => {
    setSelectedShareUrl(shareUrl);
    setQrDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteShare = async () => {
    if (!selectedShare) return;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('shared_links')
        .delete()
        .eq('id', selectedShare.id);
      
      if (error) throw error;
      
      // Remove the share from the list
      setSharedLinks(prev => prev.filter(share => share.id !== selectedShare.id));
      
      setDeleteConfirmOpen(false);
      setSuccess('Paylaşım bağlantısı başarıyla silindi.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error deleting share:', err);
      setError('Paylaşım bağlantısı silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!selectedShare) return;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('shared_links')
        .update({ 
          password: newPassword || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedShare.id);
      
      if (error) throw error;
      
      // Update the share in the list
      setSharedLinks(prev => 
        prev.map(share => 
          share.id === selectedShare.id 
            ? { ...share, password: newPassword || null } 
            : share
        )
      );
      
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSuccess('Şifre başarıyla güncellendi.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error updating password:', err);
      setError('Şifre güncellenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'view':
        return <VisibilityIcon fontSize="small" />;
      case 'comment':
        return <CommentIcon fontSize="small" />;
      case 'edit':
        return <EditIcon fontSize="small" />;
      default:
        return <VisibilityIcon fontSize="small" />;
    }
  };

  const getExpirationText = (expiresAt) => {
    if (!expiresAt) return 'Süresiz';
    
    const expDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Süresi doldu';
    if (diffDays === 1) return 'Bugün sona eriyor';
    if (diffDays < 7) return `${diffDays} gün kaldı`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta kaldı`;
    return `${Math.floor(diffDays / 30)} ay kaldı`;
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '1600px', 
      margin: '0 auto', 
      padding: '30px 20px',
      position: 'relative',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: theme => `radial-gradient(circle at 50% 50%, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
      borderRadius: '24px',
      boxShadow: theme => `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)'}`,
      border: theme => `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Paylaşımlarım
          </Typography>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : sharedLinks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ShareIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Henüz paylaşım bağlantınız bulunmuyor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Galerinizdeki görselleri veya mülklerinizi paylaşarak bağlantılar oluşturabilirsiniz.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/gallery')}
            startIcon={<CollectionsIcon />}
          >
            Galeriye Git
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>İçerik</TableCell>
                <TableCell>Tür</TableCell>
                <TableCell>İzinler</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Oluşturulma</TableCell>
                <TableCell>Görüntülenme</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sharedLinks.map((share) => (
                <StyledTableRow key={share.id}>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {share.thumbnail_url ? (
                        <Box 
                          component="img" 
                          src={share.thumbnail_url} 
                          alt={share.item_name}
                          sx={{ 
                            width: 50, 
                            height: 50, 
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border: theme => `1px solid ${theme.palette.divider}`
                          }}
                        />
                      ) : (
                        <Box sx={{ 
                          width: 50, 
                          height: 50, 
                          borderRadius: '8px',
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <ShareIcon color="disabled" />
                        </Box>
                      )}
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {share.item_name || 'İsimsiz'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={share.item_type === 'property' ? 'Mülk' : 'Görsel'} 
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={
                      share.permission === 'view' ? 'Sadece görüntüleme' : 
                      share.permission === 'comment' ? 'Görüntüleme ve yorum' : 
                      'Tam erişim'
                    }>
                      <Chip 
                        icon={getPermissionIcon(share.permission)}
                        label={
                          share.permission === 'view' ? 'Görüntüleme' : 
                          share.permission === 'comment' ? 'Yorum' : 
                          'Düzenleme'
                        }
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {share.password ? (
                        <Tooltip title="Şifre korumalı">
                          <LockIcon fontSize="small" color="action" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Şifre yok">
                          <LockOpenIcon fontSize="small" color="disabled" />
                        </Tooltip>
                      )}
                      <Chip 
                        icon={<AccessTimeIcon fontSize="small" />}
                        label={getExpirationText(share.expires_at)}
                        size="small"
                        color={isExpired(share.expires_at) ? "error" : (!share.expires_at ? "success" : "default")}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {new Date(share.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {share.view_count || 0}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title="Paylaşımı görüntüle">
                        <IconButton 
                          size="small"
                          onClick={() => window.open(`/share/${share.id}`, '_blank')}
                          disabled={isExpired(share.expires_at)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Seçenekler">
                        <IconButton 
                          size="small"
                          onClick={(e) => handleMenuOpen(e, share)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Share options menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 200,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem onClick={() => {
          const shareUrl = `${window.location.origin}/share/${selectedShare?.id}`;
          copyToClipboard(shareUrl);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bağlantıyı Kopyala</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          const shareUrl = `${window.location.origin}/share/${selectedShare?.id}`;
          handleShowQrCode(shareUrl);
        }}>
          <ListItemIcon>
            <QrCodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>QR Kod Oluştur</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          setPasswordDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            {selectedShare?.password ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {selectedShare?.password ? 'Şifreyi Değiştir' : 'Şifre Ekle'}
          </ListItemText>
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            setDeleteConfirmOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Paylaşımı Sil</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* QR Code Dialog */}
      <QRCodeDialog 
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        shareUrl={selectedShareUrl}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Paylaşımı Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu paylaşım bağlantısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
          <Button 
            onClick={handleDeleteShare} 
            color="error"
            variant="contained"
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      >
        <DialogTitle>
          {selectedShare?.password ? 'Şifreyi Değiştir' : 'Şifre Ekle'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedShare?.password 
              ? 'Paylaşım için yeni bir şifre belirleyin veya şifreyi kaldırmak için boş bırakın.'
              : 'Paylaşımınızı şifre ile koruyabilirsiniz. Şifre olmadan paylaşmak için boş bırakın.'}
          </Typography>
          <TextField
            fullWidth
            label="Şifre"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>İptal</Button>
          <Button 
            onClick={handleUpdatePassword} 
            variant="contained"
          >
            {selectedShare?.password ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharedLinks;
